<template>
    <CompassMenu
        :placement="CompassEnums.MenuPosition.BOTTOM_END"
        customClass="automation-create-automation__menu"
        :disabled="disabled"
    >
        <CompassButton
            :label="translations.labelButton"
            :loading="isLoading"
            :disabled="disabled"
            dropdown
        />
        <template #dropdown>
            <CompassMenuItemExtended
                icon="obj-slsy-basic-automation"
                :title="translations.templateTitle"
                :caption="translations.templateCaption"
                @click="handleGoToTemplates"
            />
            <CompassMenuItemExtended
                icon="obj-slsy-advanced-services"
                :title="translations.servicesTitle"
                :caption="translations.servicesCaption"
                @click="handleGoToServices"
            />
            <CompassMenuItemExtended
                v-if="hasAutomationBuilderFeature"
                icon="obj-slsy-advanced-automation"
                :title="translations.automationTitle"
                :caption="translations.automationCaption"
                @click="handleCreateAutomation"
            />
        </template>
    </CompassMenu>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Feature from 'Services/Feature';
import { createAutomation } from 'api/automation/automationService';
import type { PushToast } from 'types/injects/pushToast.type';
import type { Translator } from 'types/injects/translator.type';

interface Props {
    disabled?: boolean;
}

withDefaults(defineProps<Props>(), {
    disabled: false,
});

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const store = useStore();
const route = useRoute();
const router = useRouter();

const translations: Record<string, string> = {
    labelButton: $t.__('automation.AutomationLayout.create_automation'),
    automationTitle: $t.__('automation.AutomationLayout.create_actions_automation'),
    automationCaption: $t.__('automation.AutomationLayout.create_actions_automation_caption'),
    templateTitle: $t.__('automation.AutomationLayout.create_actions_template'),
    templateCaption: $t.__('automation.AutomationLayout.create_actions_template_caption'),
    servicesTitle: $t.__('automation.AutomationLayout.create_actions_services'),
    servicesCaption: $t.__('automation.AutomationLayout.create_actions_services_caption'),
};

const isLoading = ref(false);

const hasAutomationBuilderFeature = computed<boolean>(() =>
    Feature.isEnabled('automations-builder'),
);

const handleCreateAutomation = (): void => {
    isLoading.value = true;
    createAutomation()
        .then((response) => {
            if (response) {
                router.push({
                    name: 'automations.flow.builder',
                    params: { id: response.data.id },
                });
            }
        })
        .catch((error) => {
            store.state.system.errorsHandler.logException(error);
            $pushToast({
                type: 'error',
                close: true,
                text: $t.__('automation.AutomationLayout.error.create_automation'),
            });
        })
        .finally(() => {
            isLoading.value = false;
        });
};

const handleGoToServices = (): void => {
    window.open('https://go.sellsy.com/demande-info-sellsy-services', '_blank');
};

const handleGoToTemplates = (): void => {
    if (route.name === 'automations.templates') {
        return;
    }
    router.push({ name: 'automations.templates' });
};
</script>

<style lang="scss">
.compass-menu__dropdown__container.automation-create-automation__menu {
    z-index: 3;
}
</style>
